import { useEffect, useState, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useAuth } from './hooks/useAuth';
import { getAccessToken } from './services/api';
import { useWebSocket } from './hooks/useWebSocket';
import { useTimers } from './stores/useTimers';
import { useOfflineSync } from './hooks/useOfflineSync';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Unlock from './pages/Unlock';
import Dashboard from './pages/Dashboard';
import BabyProfile from './pages/BabyProfile';
import EventHistory from './pages/EventHistory';
import AddEvent from './pages/AddEvent';
import Settings from './pages/Settings';
import SupportHub from './pages/SupportHub';
import SupportTickets from './pages/SupportTickets';
import Documentation from './pages/Documentation';
import SharedView from './pages/SharedView';
import InviteAccept from './pages/InviteAccept';
import EmergencyAccess from './pages/EmergencyAccess';
import EmergencyLookup from './pages/EmergencyLookup';
import EmergencyRequest from './pages/EmergencyRequest';
import ExportData from './pages/ExportData';
import Reminders from './pages/Reminders';
import HealthMetrics from './pages/HealthMetrics';
import PregnancyTimeline from './pages/PregnancyTimeline';
import PregnancyProfile from './pages/PregnancyProfile';
import AddProfileChoice from './pages/AddProfileChoice';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';

// Components
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import OfflineIndicator from './components/OfflineIndicator';
import UpdateBanner from './components/UpdateBanner';
import { ThemeContext } from './context/ThemeContext';

// Theme management
function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return { theme, setTheme };
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isInitialized, needsUnlock } = useAuth();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show unlock screen if session needs password
  if (needsUnlock) {
    return <Unlock />;
  }

  return <>{children}</>;
}

// Admin route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isInitialized, needsUnlock } = useAuth();

  if (!isInitialized) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (needsUnlock) return <Unlock />;
  if (!user.isAdmin) return <Navigate to="/app/dashboard" replace />;

  return <>{children}</>;
}

// Public route wrapper (redirect to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isInitialized, needsUnlock } = useAuth();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // If user exists but needs unlock, show unlock screen
  if (user && needsUnlock) {
    return <Unlock />;
  }

  // If user is fully authenticated, go to dashboard
  if (user) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { initialize, isInitialized, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const { isOnline, pendingCount } = useOfflineSync();

  // Service worker update handling
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      if (registration) {
        // Check for updates every 60 seconds (was 10 min — too slow)
        setInterval(() => { registration.update(); }, 60 * 1000);

        // Check for updates whenever user returns to the tab
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update();
          }
        });
      }
    },
    onRegisterError(error: Error) {
      console.error('SW registration failed:', error);
    },
  });

  // Reload when a new SW takes control so the page picks up fresh precached assets.
  // Guard: only if there was already a controller (skip first-ever install).
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw?.controller) return;

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    sw.addEventListener('controllerchange', onControllerChange);
    return () => sw.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // Trigger the waiting SW to activate (if skipWaiting hasn't already fired)
  useEffect(() => {
    if (needRefresh) {
      updateServiceWorker(true);
    }
  }, [needRefresh, updateServiceWorker]);

  // Derive WebSocket URL from API base
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  const wsUrl = apiBase.replace('/api', '/ws').replace(/^http/, 'ws');
  const accessToken = user ? getAccessToken() : null;

  const { handleTimerWSMessage } = useTimers();

  useWebSocket({
    url: wsUrl,
    token: accessToken,
    onMessage: (msg: Record<string, unknown>) => {
      if (msg.type === 'event_changed' && msg.babyId) {
        queryClient.invalidateQueries({ queryKey: ['events', msg.babyId] });
        queryClient.invalidateQueries({ queryKey: ['latestEvents', msg.babyId] });
        queryClient.invalidateQueries({ queryKey: ['todayEvents', msg.babyId] });
      } else if (msg.type && typeof msg.type === 'string' && msg.type.startsWith('timer_')) {
        handleTimerWSMessage(msg);
      } else if (msg.type === 'support_ticket_changed') {
        queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
        if (msg.ticketId) {
          queryClient.invalidateQueries({ queryKey: ['support-ticket', msg.ticketId] });
        }
        // Dispatch custom event for admin pages (which use manual state, not React Query)
        window.dispatchEvent(new CustomEvent('support-ticket-ws', { detail: msg }));
      }
    },
  });

  // Initialize auth on app load
  useEffect(() => {
    initialize();
  }, [initialize]);

  const router = useMemo(() => createBrowserRouter([
    // Public routes
    { path: '/', element: <PublicRoute><Landing /></PublicRoute> },
    { path: '/login', element: <PublicRoute><Login /></PublicRoute> },
    { path: '/register', element: <PublicRoute><Register /></PublicRoute> },
    { path: '/verify-email', element: <VerifyEmail /> },
    { path: '/share/:token', element: <SharedView /> },
    { path: '/emergency', element: <EmergencyLookup /> },
    { path: '/emergency/request/:contactId', element: <EmergencyRequest /> },
    { path: '/emergency/access/:requestId', element: <EmergencyAccess /> },
    { path: '/invite/:token', element: <InviteAccept /> },
    { path: '/terms', element: <Terms /> },
    { path: '/privacy', element: <Privacy /> },

    // Protected routes (with layout)
    {
      path: '/app',
      element: <ProtectedRoute><Layout /></ProtectedRoute>,
      children: [
        { index: true, element: <Navigate to="/app/dashboard" replace /> },
        { path: 'dashboard', element: <Dashboard /> },
        { path: 'baby/new', element: <AddProfileChoice /> },
        { path: 'baby/create', element: <BabyProfile /> },
        { path: 'baby/:babyId', element: <BabyProfile /> },
        { path: 'pregnancy/new', element: <PregnancyProfile /> },
        { path: 'pregnancy/:babyId', element: <PregnancyProfile /> },
        { path: 'baby/:babyId/history', element: <EventHistory /> },
        { path: 'baby/:babyId/reminders', element: <Reminders /> },
        { path: 'baby/:babyId/health', element: <HealthMetrics /> },
        { path: 'baby/:babyId/timeline', element: <PregnancyTimeline /> },
        { path: 'baby/:babyId/event/new', element: <AddEvent /> },
        { path: 'baby/:babyId/event/:eventId', element: <AddEvent /> },
        { path: 'settings', element: <Settings /> },
        { path: 'support', element: <SupportHub /> },
        { path: 'support/tickets', element: <SupportTickets /> },
        { path: 'docs', element: <Documentation /> },
        { path: 'export', element: <ExportData /> },
      ],
    },

    // Admin routes
    { path: '/admin', element: <AdminRoute><AdminDashboard /></AdminRoute> },

    // Redirect old paths
    { path: '/dashboard', element: <Navigate to="/app/dashboard" replace /> },
    { path: '/settings', element: <Navigate to="/app/settings" replace /> },
    { path: '/support', element: <Navigate to="/app/support" replace /> },

    // 404
    { path: '*', element: <Navigate to="/" replace /> },
  ]), []); // stable router — theme decoupled via ThemeContext

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <UpdateBanner needRefresh={needRefresh} />
      <OfflineIndicator isOnline={isOnline} pendingCount={pendingCount} />
      <RouterProvider router={router} />
    </ThemeContext.Provider>
  );
}
